import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PlayCircle, Clock, CheckCircle2, Loader2 } from "lucide-react";

const RUNNABLE = [
  { id: "invite_sweep", label: "Send eligible invites", description: "Finds referrers whose order was delivered >48h ago, not in cooldown, and sends WhatsApp invite." },
  { id: "payout_sweep", label: "Process scheduled payouts", description: "Processes payouts whose scheduled_for has elapsed; sends paired M-Pesa SMS + WhatsApp." },
  { id: "daily_summary", label: "Broadcast daily summary", description: "Sends each active referrer their 24h digest via WhatsApp (only if they had activity)." },
];

export default function Scheduler() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [results, setResults] = useState({});

  const load = async () => {
    const { data } = await api.get("/scheduler/status");
    setStatus(data);
  };
  useEffect(() => { load(); }, []);

  const runNow = async (jobId) => {
    setBusy(jobId);
    try {
      const { data } = await api.post(`/scheduler/run-now/${jobId}`);
      setResults((r) => ({ ...r, [jobId]: data.result }));
      await load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
          <Clock className="h-6 w-6 text-[#66cc33]" /> Scheduler
        </h1>
        <p className="text-sm text-[#5c665a]">
          Automated background jobs. Also runnable manually — useful for demos and hotfixes.
        </p>
      </div>

      <div className="admin-card">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Status</h3>
          <span className={status?.running ? "badge-green" : "badge-pink"}>
            {status?.running ? <><CheckCircle2 className="mr-1 h-3 w-3 inline" /> running</> : "stopped"}
          </span>
        </div>
        {!status ? (
          <div className="mt-3 text-xs text-[#5c665a]">Loading…</div>
        ) : (
          <table className="mt-3 min-w-full text-sm" data-testid="scheduler-jobs">
            <thead className="text-left text-xs uppercase tracking-wider text-[#5c665a]">
              <tr><th className="py-2">Job</th><th className="py-2">Trigger</th><th className="py-2">Next run</th></tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8e2]">
              {status.jobs.map((j) => (
                <tr key={j.id}>
                  <td className="py-2 font-semibold">{j.name}</td>
                  <td className="py-2 font-mono text-xs text-[#5c665a]">{j.trigger}</td>
                  <td className="py-2 text-xs text-[#5c665a]">{new Date(j.next_run).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {RUNNABLE.map((r) => (
          <div key={r.id} className="admin-card">
            <h3 className="font-semibold">{r.label}</h3>
            <p className="mt-1 text-xs text-[#5c665a]">{r.description}</p>
            <button
              className="admin-btn mt-4 w-full"
              onClick={() => runNow(r.id)}
              disabled={busy === r.id}
              data-testid={`run-${r.id.replace(/_/g, '-')}`}
            >
              {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4" /> Run now</>}
            </button>
            {results[r.id] && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-[#fafcfa] p-3 text-[11px] text-[#3f7c21]">
                {JSON.stringify(results[r.id], null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
